import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { InvitationPreview } from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import {
  AdminPage,
  Avatar,
  Badge,
  Btn,
  Card,
  CardPad,
  Crumb,
  DefinitionList,
  Field,
  Form,
  Input,
  Note,
  PageHead,
  btnClass,
} from '../admin-kit';
import { linkFailure, tokenLooksValid } from '../../shared/password-link';
import { useSession } from '../../shared/session';
import { useMyTenants } from '../../shared/tenant-switcher';
import { useProductName } from '../../shared/theme';
import { AuthFrame, AuthIcon, AuthTitle, ClockIcon, DoneIcon, MailIcon } from './auth-frame';

/**
 * Wachtwoord vergeten (spec 01 T8 en T9).
 *
 * Het antwoord is altijd hetzelfde, ook als het account niet bestaat — precies zoals de
 * API het doet. Zou dit scherm "onbekend e-mailadres" tonen, dan was de hele
 * voorzichtigheid aan de serverkant voor niets.
 *
 * De bedrijfscode blijft staan, ook al toont de mockup ze niet: `/password/forgot` zoekt het
 * account binnen één bedrijf, en hetzelfde adres kan in meer dan één bedrijf een account hebben.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const [tenantCode, setTenantCode] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.POST('/api/v1/auth/password/forgot', {
        body: { tenantCode: tenantCode.trim(), email: email.trim() },
      });
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <AuthFrame>
        <AuthIcon tone="success" icon={<MailIcon />} />
        <AuthTitle>{t('beheer.toegang.forgot.sentTitle')}</AuthTitle>
        {/*
          De geldigheid staat in de mail en niet hier: ze is per omgeving in te stellen
          (`PASSWORD_RESET_TTL_MINUTES`) en de webapp kent ze niet.
        */}
        <p className="a-muted" role="status">
          {t('beheer.toegang.forgot.sentText')}
        </p>
        <Link to="/admin/login" className={btnClass({ variant: 'sec', size: 'lg', block: true })}>
          {t('beheer.toegang.toLogin')}
        </Link>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <Crumb crumb={{ label: t('beheer.toegang.forgot.back'), to: '/admin/login' }} />
      <AuthTitle>{t('forgot.title')}</AuthTitle>
      <p className="a-muted">{t('beheer.toegang.forgot.intro')}</p>
      <Form onSubmit={() => void submit()}>
        <Field label={t('login.tenantCode')} hint={t('login.tenantCodeHint')}>
          {(props) => (
            <Input
              {...props}
              value={tenantCode}
              onChange={(event) => setTenantCode(event.target.value)}
              required
            />
          )}
        </Field>
        <Field label={t('login.email')}>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          )}
        </Field>
        <Btn type="submit" size="lg" block disabled={busy} aria-busy={busy || undefined}>
          {t('beheer.toegang.forgot.submit')}
        </Btn>
      </Form>
    </AuthFrame>
  );
}

/**
 * Het token uit de link in de mail, één keer gelezen en daarna uit de adresbalk gehaald.
 *
 * Een token in de URL blijft anders in de browsergeschiedenis staan, en op een gedeelde computer
 * is dat een sleutel voor wie na je komt. Het token leeft vanaf hier alleen in het geheugen van
 * deze pagina; de overige parameters (`to`) blijven staan.
 */
function useTokenFromUrl(fromSearch: string | undefined): string {
  const [token] = useState(fromSearch ?? '');
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);
  return token;
}

/** Nieuw wachtwoord kiezen via de link uit de mail (spec 01 T10). */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const search = useSearch({ strict: false }) as { token?: string };
  const token = useTokenFromUrl(search.token);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  // Een link zonder token of met een ingekort token werkt nooit: meteen zeggen, niet pas na het
  // kiezen van een wachtwoord (testrapport 28 okt., fout 19).
  const [state, setState] = useState<'form' | 'done' | 'invalid'>(() =>
    tokenLooksValid(token) ? 'form' : 'invalid',
  );
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    // Twee keer hetzelfde, nagekeken voor er iets naar de server gaat: een tikfout hier is een
    // wachtwoord dat niemand kent.
    if (password !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    try {
      const { error: failure, response } = await api.POST('/api/v1/auth/password/reset', {
        body: { token, password },
      });
      if (!failure) {
        setState('done');
        return;
      }
      // 409 en 404 gaan over de link zelf, 400 over het gekozen wachtwoord - of over het token.
      if (linkFailure(response.status, failure) === 'password') setError(t('invite.failed'));
      else setState('invalid');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'done') {
    return (
      <AuthFrame>
        <AuthIcon tone="success" icon={<DoneIcon />} />
        <AuthTitle>{t('beheer.toegang.reset.doneTitle')}</AuthTitle>
        <p className="a-muted" role="status">
          {t('forgot.resetDone')}
        </p>
        <Link to="/admin/login" className={btnClass({ size: 'lg', block: true })}>
          {t('beheer.toegang.toLogin')}
        </Link>
      </AuthFrame>
    );
  }

  if (state === 'invalid') {
    return (
      <AuthFrame>
        <AuthIcon tone="danger" icon={<ClockIcon />} />
        <AuthTitle>{t('beheer.toegang.reset.invalidTitle')}</AuthTitle>
        <p className="a-muted" role="alert">
          {t('forgot.resetInvalid')}
        </p>
        <Link
          to="/admin/forgot-password"
          className={btnClass({ variant: 'sec', size: 'lg', block: true })}
        >
          {t('beheer.toegang.reset.newLink')}
        </Link>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthTitle>{t('beheer.toegang.reset.title')}</AuthTitle>
      <Form onSubmit={() => void submit()}>
        <Field
          label={t('beheer.toegang.reset.password')}
          hint={t('beheer.toegang.reset.hint')}
          error={error ?? undefined}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              placeholder={t('beheer.toegang.minLength')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          )}
        </Field>
        <Field
          label={t('beheer.toegang.reset.repeat')}
          error={mismatch ? t('beheer.toegang.mismatch') : undefined}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
              required
            />
          )}
        </Field>
        <Note tone="attention">{t('beheer.toegang.reset.signsOut')}</Note>
        <Btn type="submit" size="lg" block disabled={busy} aria-busy={busy || undefined}>
          {t('beheer.toegang.reset.submit')}
        </Btn>
      </Form>
    </AuthFrame>
  );
}

/**
 * Een uitnodiging aanvaarden (spec 01 T5 nieuw account, T6 bestaand account, T7 verlopen):
 * wachtwoord kiezen of bevestigen, en meteen aangemeld door naar het beheer.
 */
export function AcceptInvitePage() {
  const { t } = useTranslation();
  const { api, adoptToken } = useSession();
  const navigate = useNavigate();
  // Hetzelfde adres in een ander bedrijf (ACC-2): bevestigen met het bestaande wachtwoord, of toch
  // een eigen wachtwoord kiezen.
  const [mode, setMode] = useState<'choose' | 'confirm'>('choose');
  const [goneText, setGoneText] = useState<string | null>(null);
  // `to=app`: een gewone medewerker (LOG-1) komt na het wachtwoord in de app en niet in het beheer.
  const search = useSearch({ strict: false }) as { token?: string; to?: string };
  const token = useTokenFromUrl(search.token);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [state, setState] = useState<'loading' | 'form' | 'invalid' | 'revoked'>('loading');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data, error: failure } = await api.GET('/api/v1/auth/invitations/preview', {
        params: { query: { token } },
      });
      if (!active) return;
      if (failure || !data) {
        /*
          Ingetrokken krijgt zijn eigen scherm (Extra 4).

          "Deze uitnodiging is niet meer geldig" zou kloppen en de ontvanger laten denken dat hij
          te laat was. Hij was niet te laat: iemand heeft hem ingetrokken, en dan hoort hij zijn
          beheerder aan te spreken en niet opnieuw op de link te klikken.
        */
        setState(problemCode(failure) === 'GONE' ? 'revoked' : 'invalid');
        setGoneText((failure as { detail?: string } | undefined)?.detail ?? null);
        return;
      }
      setPreview(data as InvitationPreview);
      if ((data as InvitationPreview).existingAccount) setMode('confirm');
      setState('form');
    })();
    return () => {
      active = false;
    };
  }, [api, token]);

  const submit = async () => {
    setError(null);
    // Een nieuw wachtwoord twee keer; een bestaand niet, dat kent de persoon al.
    if (mode === 'choose' && password !== repeat) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    try {
      const path =
        mode === 'confirm' ? '/api/v1/auth/invitations/confirm' : '/api/v1/auth/invitations/accept';
      const {
        data,
        error: failure,
        response,
      } = await api.POST(path, {
        body: { token, password },
      });
      if (!failure && data) {
        // Meteen aangemeld (ACC-2), en door naar het beheer.
        await adoptToken((data as { accessToken: string }).accessToken);
        void navigate({ to: search.to === 'app' ? '/app' : '/admin' });
        return;
      }
      if (response.status === 401) setError(t('invite.confirmFailed'));
      else if (linkFailure(response.status, failure) === 'password') setError(t('invite.failed'));
      // 410 betekent ingetrokken; dat is iets anders dan een verlopen of verzonnen link, en
      // de uitgenodigde moet weten dat hij een nieuwe moet vragen en niet moet wachten.
      else if (response.status === 410) {
        setGoneText((failure as { detail?: string } | undefined)?.detail ?? null);
        setState('revoked');
      } else setState('invalid');
    } finally {
      setBusy(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'confirm' ? 'choose' : 'confirm');
    setError(null);
    setMismatch(false);
    setPassword('');
    setRepeat('');
  };

  if (state === 'loading') {
    return (
      <AuthFrame>
        <LoadingState rows={3} label={t('common.loading')} />
      </AuthFrame>
    );
  }

  if (state === 'revoked' || state === 'invalid') {
    const revoked = state === 'revoked';
    return (
      <AuthFrame>
        <AuthIcon tone="danger" icon={<ClockIcon />} />
        <AuthTitle>
          {revoked ? t('beheer.toegang.invite.revokedTitle') : t('beheer.toegang.invite.goneTitle')}
        </AuthTitle>
        <p
          className="a-muted"
          role="alert"
          data-testid={revoked ? 'invite-revoked' : 'invite-invalid'}
        >
          {revoked
            ? (goneText ?? t('invitations.revokedPage'))
            : t('beheer.toegang.invite.goneText')}
        </p>
        <Link to="/admin/login" className={btnClass({ variant: 'sec', size: 'lg', block: true })}>
          {t('beheer.toegang.toLogin')}
        </Link>
      </AuthFrame>
    );
  }

  const tenant = preview?.tenantName ?? '';
  const email = preview?.email ?? '';

  return (
    <AuthFrame>
      <Badge tone="info">{t('beheer.toegang.invite.badge')}</Badge>
      <AuthTitle>
        {mode === 'confirm'
          ? t('beheer.toegang.invite.confirmTitle', { tenant })
          : t('beheer.toegang.invite.newTitle', { tenant })}
      </AuthTitle>
      {/*
        Wie uitnodigde en met welke rol, toont de mockup ook ("Sarah De Backer nodigde je uit als
        Communicatie"). De voorvertoning van de uitnodiging kent die twee niet; het adres wel.
      */}
      <p
        className="a-muted"
        data-testid={mode === 'confirm' ? 'invite-confirm-hint' : 'invite-new-hint'}
      >
        <Trans
          i18nKey={
            mode === 'confirm'
              ? 'beheer.toegang.invite.confirmText'
              : 'beheer.toegang.invite.newText'
          }
          values={{ email }}
          components={{ strong: <strong /> }}
        />
      </p>
      <Form onSubmit={() => void submit()}>
        {mode === 'confirm' ? (
          <Field label={t('invite.existingPassword')} error={error ?? undefined}>
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            )}
          </Field>
        ) : (
          <>
            <Field
              label={t('beheer.toegang.invite.password')}
              hint={t('beheer.toegang.invite.passwordHint')}
              error={error ?? undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('beheer.toegang.minLength')}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              )}
            </Field>
            <Field
              label={t('beheer.toegang.invite.repeat')}
              error={mismatch ? t('beheer.toegang.mismatch') : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  value={repeat}
                  onChange={(event) => setRepeat(event.target.value)}
                  required
                />
              )}
            </Field>
          </>
        )}
        <Btn type="submit" size="lg" block disabled={busy} aria-busy={busy || undefined}>
          {mode === 'confirm' ? t('invite.confirm') : t('beheer.toegang.invite.submit')}
        </Btn>
      </Form>
      {/*
        Een bestaand account kan ook een eigen wachtwoord voor dit bedrijf kiezen (ACC-2); die keuze
        staat niet in de mockup en krijgt een plaats bij de links onder het formulier.
      */}
      {mode === 'confirm' || preview?.existingAccount ? (
        <div className="a-auth-links">
          {mode === 'confirm' ? (
            <Link to="/admin/forgot-password" className="a-link">
              {t('login.forgot')}
            </Link>
          ) : null}
          {preview?.existingAccount ? (
            <button type="button" className="a-link" onClick={switchMode}>
              {mode === 'confirm' ? t('invite.ownPassword') : t('invite.useExisting')}
            </button>
          ) : null}
        </div>
      ) : null}
    </AuthFrame>
  );
}

/**
 * Wachtwoord wijzigen, in de schil (spec 01 T11): wie ben ik en bij welk bedrijf, en daaronder het
 * formulier. Bereikbaar via het avatarmenu.
 */
export function AccountPage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tenants = useMyTenants().data ?? [];

  const submit = async () => {
    setError(null);
    setBusy(true);
    try {
      const { error: failure } = await session.api.PUT('/api/v1/auth/password', {
        body: { currentPassword, newPassword },
      });
      if (failure) {
        setError(t('account.changeFailed'));
        return;
      }
      // Wijzigen trekt alle sessies in, ook deze. Meteen terug naar het aanmeldscherm.
      await session.logout();
      void navigate({ to: '/admin/login' });
    } finally {
      setBusy(false);
    }
  };

  const profile = session.profile;
  const name = profile?.displayName ?? '—';
  const tenantName = tenants.find((item) => item.current)?.name;
  const productName = useProductName();
  const tenantCode = profile?.tenantCode ?? productName;

  return (
    <AdminPage narrow>
      <PageHead title={t('beheer.shell.changePassword')} />
      {/*
        Wie ben ik, en bij welk bedrijf (punt 18).

        Het scherm heette "Mijn account" en toonde alleen twee wachtwoordvelden. Bij een beheerder
        die voor twee klanten werkt, is "bij welk bedrijf ben ik nu aangemeld" geen overbodige
        vraag - zeker niet vlak voordat hij iets wijzigt.
      */}
      <Card testId="account-identity" label={name}>
        <CardPad>
          <div className="a-person">
            <Avatar name={name} className="a-account-avatar" />
            <div>
              <div className="a-name" style={{ fontSize: 16 }}>
                {name}
              </div>
              {/* De rolnaam kent de sessie niet; "Beheer" klopt altijd (zoals in de schil). */}
              <div className="a-meta">{t('beheer.toegang.account.role')}</div>
            </div>
          </div>
          <DefinitionList
            items={[
              [t('login.email'), profile?.email ?? '—'],
              [
                t('dashboard.companyLabel'),
                tenantName ? (
                  <>
                    {tenantName} · <span className="a-mono">{tenantCode}</span>
                  </>
                ) : (
                  <span className="a-mono">{tenantCode}</span>
                ),
              ],
            ]}
          />
          <div className="a-account-rule" />
          <Form onSubmit={() => void submit()} className="max-w-[420px]">
            <Field label={t('account.currentPassword')}>
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                />
              )}
            </Field>
            <Field
              label={t('account.newPassword')}
              hint={t('beheer.toegang.account.hint')}
              error={error ?? undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('beheer.toegang.minLength')}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                />
              )}
            </Field>
            {/*
              De waarschuwing staat **voor** de knop en niet erna (punt 18).

              Een wachtwoord wijzigen trekt elke sessie in, ook die op de telefoon in de kleedkamer.
              Dat achteraf melden is geen melding maar een verrassing.
            */}
            <Note tone="attention">{t('beheer.toegang.account.signsOut')}</Note>
            <div>
              <Btn type="submit" disabled={busy} aria-busy={busy || undefined}>
                {t('account.changePassword')}
              </Btn>
            </div>
          </Form>
        </CardPad>
      </Card>
    </AdminPage>
  );
}
